import { useEffect, useMemo, type ReactNode } from 'react';
import { useUser } from '@/lib/auth/react';
import { createAnalyticsClient, createNoopClient, setAnalyticsClient, type AnalyticsClient } from './client';
import { buildAnalyticsInstance } from './instance';
import { reconcileIdentity } from './identity';
import { AnalyticsContext } from './context';
import { IS_E2E } from '@/test-support/e2e-mode';

const APP_NAME = 'predictions-ui';

let singleton: AnalyticsClient | null = null;

/**
 * Build the singleton analytics client over an `analytics` instance. Each
 * destination is independently env-gated inside `buildAnalyticsInstance` —
 * Mixpanel via `VITE_MIXPANEL_TOKEN`, GTM via `VITE_GTM_CONTAINER_ID`.
 *
 * With neither set (and not under Playwright) there is nothing to send, so we
 * short-circuit to the no-op client — avoids spinning up an empty instance in
 * local dev. Under Playwright (IS_E2E === true) the instance carries only the
 * capture plugin, so smoke tests read `window.__analyticsE2EEvents` and no real
 * destinations (or ad scripts) load.
 */
function getDefaultClient(): AnalyticsClient {
  if (singleton) return singleton;
  const env = {
    app: APP_NAME,
    appVersion: (import.meta.env.VITE_APP_VERSION ?? '0.0.0') as string,
    mixpanelToken: (import.meta.env.VITE_MIXPANEL_TOKEN ?? '') as string,
    gtmContainerId: (import.meta.env.VITE_GTM_CONTAINER_ID ?? '') as string,
  };

  singleton =
    !IS_E2E && !env.mixpanelToken && !env.gtmContainerId
      ? createNoopClient()
      : createAnalyticsClient(buildAnalyticsInstance(env));

  setAnalyticsClient(singleton);
  return singleton;
}

interface ProviderProps {
  client?: AnalyticsClient;
  children: ReactNode;
}

/**
 * Mount once at the top of the app. Wires identity reconciliation to
 * the auth user profile. `client` is exposed for tests so they can
 * inject a fake without touching module state.
 */
export function AnalyticsProvider({ client, children }: ProviderProps): ReactNode {
  const resolved = useMemo(() => {
    const c = client ?? getDefaultClient();
    // Keep the non-React singleton in sync with whatever the provider
    // resolved — covers test injection and the default singleton path.
    setAnalyticsClient(c);
    return c;
  }, [client]);
  const user = useUser();

  useEffect(() => {
    reconcileIdentity(resolved, user ?? null);
  }, [user, resolved]);

  useEffect(() => {
    resolved.register({
      surface: 'web',
      appVersion: (import.meta.env.VITE_APP_VERSION ?? '0.0.0') as string,
    });
  }, [resolved]);

  return <AnalyticsContext.Provider value={resolved}>{children}</AnalyticsContext.Provider>;
}

/**
 * Test-only provider that injects a fake analytics client into the
 * React tree without going through the real AnalyticsProvider (which
 * pulls in useUser and side effects). Also mirrors the fake into the
 * module-level singleton so non-React consumers (MutationCache callbacks)
 * see the same instance.
 */
export function AnalyticsContextForTests({ client, children }: { client: AnalyticsClient; children: ReactNode }): ReactNode {
  setAnalyticsClient(client);
  return <AnalyticsContext.Provider value={client}>{children}</AnalyticsContext.Provider>;
}
